import React from 'react';
import { Typography, Container, Link, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { translate } from 'translations/translator';
import { useSelector } from 'react-redux';
import { AppStoreState, LESSON_PLANNER_MODE } from 'types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { faCalendarDay } from '@fortawesome/pro-regular-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { buildSearchQueryParams } from 'util/query-params-util';
import { MANAGE_TEACHING_ORDER_COMPONENT } from 'containers/ManageOptions/components/types';
import L from 'translations/variants/lessonPlanner';
import { HwtButton } from 'components/@common/Buttons/HwtButton';

const getTranslation = key => translate('lessonPlanner', key);

type Props = {
  title: string;
  lessonWeek: string;
  handleNextClick: () => void;
  handlePreviousClick: () => void;
  handleGoToTodayClick: () => void;
};
export const LessonPlannerNavbar: React.FC<Props> = ({
  title,
  lessonWeek,
  handleNextClick,
  handlePreviousClick,
  handleGoToTodayClick
}) => {
  const theme = useTheme();
  const classes = getStyles(theme)();
  const location = useLocation();
  const navigate = useNavigate();

  const lessonPlanView = useSelector(({ lessonPlanner }: AppStoreState) => lessonPlanner.view);
  const isLessonPlannerView = useSelector(
    ({ lessonPlanner }: AppStoreState) => lessonPlanner.mode === LESSON_PLANNER_MODE.LESSON_PLANNER
  );
  const isDayView = () => lessonPlanView === 'day';

  const onClickLessonName = () => {
    navigate({
      pathname: `${location.pathname}/manage-options`,
      search: buildSearchQueryParams({
        selected: MANAGE_TEACHING_ORDER_COMPONENT.ManageTeachingOrders
      })
    });
  };

  return (
    <>
      <Container className={classes.header} maxWidth="xl">
        <HwtButton
          variant="contained"
          color="primary"
          onClick={handlePreviousClick}
          className={classes.navButtons}
        >
          <FontAwesomeIcon
            size="1x"
            style={{ marginRight: 10, marginBottom: 2 }}
            icon={faChevronLeft}
          />
          {isDayView() ? getTranslation(L.previousDay) : getTranslation(L.previousWeek)}
        </HwtButton>
        <div className={classes.center}>
          <span>
            {lessonWeek}
            <FontAwesomeIcon
              size="sm"
              className={classes.todayButton}
              icon={faCalendarDay}
              onClick={handleGoToTodayClick}
              title={getTranslation(L.gotoToday)}
            />
          </span>
          {isLessonPlannerView && (
            <Link
              style={{
                display: 'flex',
                flex: 1,
                color: '#000000',
                cursor: 'pointer',
                textDecoration: 'none'
              }}
              onClick={onClickLessonName}
            >
              <Typography variant="h5">{title}</Typography>
            </Link>
          )}
        </div>

        <HwtButton
          variant="contained"
          color="primary"
          className={classes.navButtons}
          onClick={handleNextClick}
        >
          {isDayView() ? getTranslation(L.nextDay) : getTranslation(L.nextWeek)}
          <FontAwesomeIcon
            size="1x"
            style={{ marginLeft: 10, marginBottom: 2 }}
            icon={faChevronRight}
          />
        </HwtButton>
      </Container>
    </>
  );
};

const getStyles = theme =>
  makeStyles({
    header: {
      display: 'flex !important',
      flex: 1,
      justifyContent: 'space-between',
      height: 85,
      backgroundColor: theme.palette.primary.light,
      padding: '0 20px',
      whiteSpace: 'nowrap',
      flexWrap: 'revert',
      position: 'sticky',
      top: 60,
      borderBottom: `1.5px solid ${theme.palette.primary.main}`,
      zIndex: 5
    },
    todayButton: {
      marginLeft: 20,
      cursor: 'pointer'
    },
    navButtons: {
      margin: '20px !important',
      width: 'max-content !important',
      minWidth: '165px !important'
    },
    center: {
      fontSize: '28px',
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
      textAlign: 'center'
    }
  });
